<script lang="ts" setup>
import { PropType, Ref, computed, inject, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { DragModel } from '../../../tir-pm-kanban';
import {
  TirPmContextMenu,
  TirPmContextMenuButton,
  TirPmContextMenuMouseButtonEnum,
  TirPmNotificationEventBus,
  TirPmNotificationModel,
  TirPmRouterLink,
  TirPmTooltipElement,
} from 'tir-components';
import { GroupedTaskItemModel, TaskModel } from '../../../tasks-kanban/models';
import { RouterLink, useRouter } from 'vue-router';
import { RemoveTaskModal, WeeekTag } from '@/components';
import { ListItemModel } from './../../models';
import { TasksKanbanGrouppingTypeEnum, TaskViewElementEnum } from '@/components/tasks-kanban/enums';
import {
  TaskCardAuthor,
  TaskCardAttachedFiles,
  TaskCardDateCreate,
  TaskCardDeadline,
  TaskCardDifficulty,
  TaskCardObservers,
  TaskCardPerformers,
  TaskCardPriority,
  TaskCardQueue,
  TaskCardStatus,
  TaskCardTags,
  TaskCardType,
  TaskCardExpand,
  TaskCardGrouppingParam,
} from './components';
import {
  ArrowsPointingOutIcon,
  Bars3BottomLeftIcon,
  CurtainIcon,
  ListBulletIcon,
  MapIcon,
  ShareIcon,
  TrashIcon,
} from 'tir-style-system/icons/outline';
import NamespaceIconView from '@/pages/project-namespaces-page/components/namespace-card/namespace-icon-view.vue';
import TaskCardWorkTime from './components/task-card-work-time.vue';
import TaskCardEstimateTime from './components/task-card-estimate-time.vue';
import { copyToClipboard } from '@/components/task-forms/utils';
import { TaskImportServiceEnum } from '@/enums';
import PerformerListByOrder from '@/components/tasks-kanban/components/PerformerListByOrder.vue';
import GetAccessCurrentUserInQueueTaskByIdResponseModel from '../../models/GetAccessCurrentUserInQueueTaskByIdResponseModel';

/**Маршруты */
const router = useRouter();

/**Параметры */
const props = defineProps({
  /**Данные о группе */
  group: { type: Object as PropType<GroupedTaskItemModel> },
  /**Тип группировки */
  grouppingType: { type: Number as PropType<TasksKanbanGrouppingTypeEnum> },
  /**Задача */
  task: { type: Object as PropType<DragModel<TaskModel>> },
  /**Текущая задача перетаскивается */
  isTaskDragging: { type: Boolean, default: false },
  /**Задача перетаскивается над текущей */
  isOver: { type: Boolean, default: false },
  /**Уровень вложенности */
  deepLevel: { type: Number, default: 0 },
  /**Размер имени задачи */
  nameWidth: { type: String },
  /**Размеры колонок списка */
  listColumns: { type: Array as PropType<ListItemModel[]> },
  /**Разрешено ли перетаскивание */
  dragAllowed: { type: Boolean },
  /**Видимость данных группировки в карточке */
  visibleTaskGroupData: { type: Boolean, default: false },
  /**Доступы юзера к очереди */
  userAccess: { type: Object as PropType<GetAccessCurrentUserInQueueTaskByIdResponseModel> },
});
/**События */
const emit = defineEmits<{
  /**Открыть задачу */
  open: [taskId: number];
  /**Переключение видимости задачи из текущей очереди */
  current: [];
  /**Переключение видимости задачи из других очередей */
  other: [];
  /**Удалить задачу */
  removeTask: [taskId: number];
}>();

let observer: ResizeObserver | null = null;
/**Id очереди */
const queueId = inject('queueId') as Ref<string>;
/**Ссылка на компонент контекстного меню */
const $contextMenu = ref<InstanceType<typeof TirPmContextMenu>>();
/**Зажата ли ЛКМ для перетаскивания */
const isMouseDown = ref(false);
/**DOM левого блока */
const $titleBlock = ref<HTMLElement>();
/**Перетаскивается ли элемент */
const isDragging = ref(false);
/**Является ли элмент ссылкой */
const isLink = ref(true);
/**Окно удаления задачи */
const $removeTaskModal = ref<InstanceType<typeof RemoveTaskModal>>();
/**Ширина блока названия тикета */
const widthBlockName = ref<number>();
/**Является ли юзер админом проекта */
const isProjectAdmin = inject('isAdminProject') as Ref<boolean>;
/**Является ли юзер овнером */
const isOwner = inject('isOwner') as Ref<boolean>;

/**Классы компонента */
const componentClasses = computed(() => ({
  plug: props.isTaskDragging,
  over: props.isOver,
}));
/**Стили для обертки левой части карточки */
const leftPartStyle = computed(() => {
  const width = `calc(${props.nameWidth} - ${40 * props.deepLevel}px)`;

  return {
    left: leftIndent.value + 'px',
    width,
    'min-width': width,
    'max-width': width,
  };
});
/**Классы левой части компонента */
const leftPartClasses = computed(() => ({
  'tir-pm-left-border': props.task.Data.ParentTaskId || props.task.Data.SubtasksCount,
  'current-queue': props.task.Data.CheckIsCurrentQueue(queueId.value),
}));
/**Отступ слева */
const leftIndent = computed(() => {
  let indent = 40;
  if (props.deepLevel) {
    indent += 40 * props.deepLevel;
  }
  return indent;
});
/**Был ли обрезан текст */
const isTextTruncated = computed(() => widthBlockName.value >= 322);
/**Доступ к добавлению задачи в очередность исполнителя */
const accessAddQueue = computed(() => props.userAccess?.CanCustomizeOrder || isOwner.value);

/**Стили плавающего блока */
const spaceStyle = computed(() => {
  const width = leftIndent.value + 'px';
  return { width };
});
/**Отслеживание изменения названия тикета */
watch(
  () => props.task.Data.Name,
  async () => {
    await nextTick();
    widthBlockName.value = $titleBlock.value?.clientWidth;
  },
  { immediate: true },
);

/**Получение компонента для параметра задачи */
const getTaskParameterComponent = (type: TaskViewElementEnum) => {
  switch (type) {
    case TaskViewElementEnum.AttachedFiles:
      return TaskCardAttachedFiles;
    case TaskViewElementEnum.Author:
      return TaskCardAuthor;
    case TaskViewElementEnum.DateCreate:
      return TaskCardDateCreate;
    case TaskViewElementEnum.Deadline:
      return TaskCardDeadline;
    case TaskViewElementEnum.Difficulty:
      return TaskCardDifficulty;
    case TaskViewElementEnum.Observers:
      return TaskCardObservers;
    case TaskViewElementEnum.Performers:
      return TaskCardPerformers;
    case TaskViewElementEnum.Priority:
      return TaskCardPriority;
    case TaskViewElementEnum.Queue:
      return TaskCardQueue;
    case TaskViewElementEnum.Status:
      return TaskCardStatus;
    case TaskViewElementEnum.Tags:
      return TaskCardTags;
    case TaskViewElementEnum.Type:
      return TaskCardType;
    case TaskViewElementEnum.EstimateTime:
      return TaskCardEstimateTime;
    case TaskViewElementEnum.WorkTime:
      return TaskCardWorkTime;
  }
};

/**Открыть задачу */
const openTaskInDrawer = async () => {
  isLink.value = false;
  await waitMouseUp();
  if (isDragging.value) return;
  $contextMenu.value?.close();
  emit('open', props.task.Data.Id);
  isLink.value = true;
};
/**Открыть карту проекта */
const openSchema = computed(() => {
  return router.resolve({
    name: 'project-map',
    params: {
      taskId: props.task.Data.Id,
      projectId: props.task.Data.ProjectId,
    },
  });
});
/**Открыть задачу в шторке */
const openTask = computed(() => {
  const routeParams = router.currentRoute.value.params;
  const projectId = routeParams.projectId;

  return router.resolve({
    name: 'task-detail',
    params: {
      projectId: projectId,
      queueId: queueId.value,
      taskId: props.task.Data.Id,
    },
  });
});
/**Переключение разрешения на перетаскивание */
const toggleAllowDrag = (allow: boolean) => {
  if (isMouseDown.value && !allow) return;
  props.task.AllowDrag = props.dragAllowed ? allow : false;
};
/**Зажата ли ЛКМ для перетаскивания */
const toggleMouseDown = (isDown: boolean) => {
  isMouseDown.value = isDown;
};

/**Добавить оповещение */
const addNotification = (message: string, isError: boolean = false, life?: number) => {
  TirPmNotificationEventBus.emit(
    'add',
    new TirPmNotificationModel({
      title: message,
      type: isError ? 'error' : 'success',
      group: 'general',
      life: life ?? 2000,
      closable: true,
    }),
  );
};
/**Перетаскивается ли элемент */
const waitMouseUp = () => {
  return new Promise<void>((resolve) => {
    isDragging.value = false;

    const startTime = Date.now();

    const onMouseUp = () => {
      const duration = Date.now() - startTime;

      isDragging.value = duration >= 300;

      document.removeEventListener('mouseup', onMouseUp);
      isLink.value = true;
      resolve();
    };

    document.addEventListener('mouseup', onMouseUp, { once: true });
  });
};
/**Скопировать название и ссылку */
const copyNameAndLinkTask = async () => {
  const routeParams = router.currentRoute.value.params;
  const projectId = routeParams.projectId;

  if (!projectId || !queueId) return;

  const route = router.resolve({
    name: 'task-detail',
    params: {
      projectId: projectId,
      queueId: queueId.value,
      taskId: props.task.Data.Id,
    },
  });
  if (!route?.href) return;
  const origin =
    window.location.hostname === 'localhost' ? 'https://beta.hub-pm.com' : window.location.origin;

  const link = origin + route.href;
  const textContent = `${props.task.Data.Id} ${props.task.Data.Name}`;

  if (await copyToClipboard(`[${textContent}](${link})`)) {
    $contextMenu.value.close();
    addNotification('Название и ссылка тикета скопирована');
    console.log(8);
  }
};

/**Открыть окно удаления задачи */
const openRemoveModal = (id: number, name: string) => {
  $contextMenu.value.close();
  $removeTaskModal.value?.open(id, name);
};
/**Удаление задачи из списка */
const onSuccessRemove = (taskId: number) => {
  emit('removeTask', taskId);
};
</script>
<template>
  <TirPmContextMenu
    class="task-card_context"
    ref="$contextMenu"
    min-width="24.25rem"
    maxHeight="max-content"
    :trigger-buttons="[TirPmContextMenuMouseButtonEnum.Right]"
  >
    <TirPmRouterLink
      class="task-card"
      :class="componentClasses"
      :to="openTask"
      :isDisabled="!isLink"
      @dragstart.prevent
      @mousedown.left="openTaskInDrawer"
    >
      <div class="task-card_space" :style="spaceStyle" @click.stop />
      <div class="task-card_content">
        <div
          class="task-card-left-part"
          :class="leftPartClasses"
          :style="leftPartStyle"
          @mousemove="toggleAllowDrag(true)"
          @mouseleave="toggleAllowDrag(false)"
          @mousedown="toggleMouseDown(true)"
          @mouseup="toggleMouseDown(false)"
        >
          <div v-for="namespace in task?.Data?.NamespaceInfo" class="task-card_queue-block">
            <NamespaceIconView
              :padding="1"
              class="task-card_queue-icon"
              :icon="'https://beta-pm2.tirscript.com' + namespace.IconUrl"
              :color="namespace.Color"
            />
            <div class="task-card_queue-content">
              <ListBulletIcon class="task-card_icon" />
              <span v-text="namespace.Name" />
            </div>
          </div>
          <span v-text="task?.Data?.Id" class="tir-pm-task-id" />
          <WeeekTag
            v-if="task.Data.ImportService === TaskImportServiceEnum.Weeek"
            class="task-card__weeek-tag"
          />
          <TaskCardGrouppingParam
            v-if="visibleTaskGroupData"
            :status="
              grouppingType === TasksKanbanGrouppingTypeEnum.Status ? task.Data.Status : undefined
            "
            :priority="
              grouppingType === TasksKanbanGrouppingTypeEnum.Priority &&
              task.Data.Priority.Id !== undefined
                ? task.Data.Priority
                : undefined
            "
            :type="grouppingType === TasksKanbanGrouppingTypeEnum.Type ? task.Data.Type : undefined"
            :performers="
              grouppingType === TasksKanbanGrouppingTypeEnum.Performer
                ? task.Data.Performers
                : undefined
            "
            :author="
              grouppingType === TasksKanbanGrouppingTypeEnum.Author ? task.Data.Author : undefined
            "
            :tags="grouppingType === TasksKanbanGrouppingTypeEnum.Tag ? task.Data.Tags : undefined"
          />

          <div class="tir-pm-task__block-title" ref="$titleBlock">
            <TirPmTooltipElement
              :isDisabled="!isTextTruncated"
              maxWidth="23rem"
              class="tir-pm-task__tooltip-title"
            >
              <div class="tir-pm-task__title">
                {{ task?.Data?.Name }}
              </div>
              <template #content>{{ task?.Data?.Name }}</template>
            </TirPmTooltipElement>
          </div>
          <TaskCardExpand :task="task.Data" @current="emit('current')" @other="emit('other')" />
        </div>
        <div class="task-card-right-part">
          <Component
            v-for="element in listColumns"
            shortInfoParameter
            :is="getTaskParameterComponent(element.Type)"
            :element="element"
            :task="task.Data"
          />
        </div>
      </div>
    </TirPmRouterLink>
    <template #content>
      <RouterLink :to="openTask" target="_blank">
        <TirPmContextMenuButton>
          <div class="task-card__menu-item">
            <ArrowsPointingOutIcon class="task-card__menu-icon" />
            Открыть в новой вкладке
          </div>
        </TirPmContextMenuButton>
      </RouterLink>
      <TirPmRouterLink :to="openSchema" :isDisabled="!props.task?.Data">
        <TirPmContextMenuButton>
          <div class="task-card__menu-item">
            <MapIcon class="task-card__menu-icon" />
            Посмотреть карту проекта
          </div>
        </TirPmContextMenuButton>
      </TirPmRouterLink>
      <TirPmContextMenuButton @click="copyNameAndLinkTask" class="task-card__menu-item">
        <ShareIcon class="task-card__menu-icon" />
        Скопировать название и ссылку
      </TirPmContextMenuButton>
      <PerformerListByOrder
        v-if="accessAddQueue"
        :taskInfo="task.Data"
        @closePopover="$contextMenu.close()"
      />
      <TirPmContextMenuButton
        v-if="isProjectAdmin || isOwner"
        @click="openRemoveModal(task.Data.Id, task.Data.Name)"
      >
        <div class="task-card__menu-item">
          <TrashIcon class="task-card__menu-icon task-card__negative" />
          <span class="task-card__negative">Удалить тикет</span>
        </div>
      </TirPmContextMenuButton>
    </template>
  </TirPmContextMenu>
  <RemoveTaskModal ref="$removeTaskModal" @successRemove="onSuccessRemove" />
</template>
<style lang="scss">
.tir-pm-task__tooltip-title {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.task-card {
  width: 100%;
  display: flex;
  align-items: center;
  row-gap: 1.5rem;
  background-color: var(--bg-base, #fff);
  border-left: none;
  position: relative;
  color: var(--text-primary, #272d37);
  cursor: pointer;

  &__weeek-tag {
    margin-right: 0.5rem;
    flex-shrink: 0;
  }

  &__negative {
    color: var(--text-negative);
  }

  &__menu-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__menu-icon {
    width: 1.5rem;
    min-width: 1.5rem;
    height: 1.5rem;
  }

  &_queue-icon {
    margin-right: 0.75rem;

    .namespace-icon-view__icon {
      width: 1.125rem;
      height: 1.125rem;
    }
  }

  &_queue-block {
    display: flex;
    align-items: center;
    min-width: 13rem;
  }

  &_queue-content {
    display: flex;
    align-items: center;
    color: var(--text-secondary, #686c73);
    gap: 0.25rem;
  }

  &_content {
    display: flex;
    align-items: center;
    row-gap: 1.5rem;
    flex: 1;

    &:hover {
      .task-card-left-part,
      .task-card-right-part {
        border-color: var(--neutral-40);
      }
    }

    &:active {
      .task-card-left-part,
      .task-card-right-part {
        border-color: var(--neutral-90);
      }
    }
  }

  &_context {
    width: 100%;
  }

  .tir-pm-task-id {
    padding-right: 2.75rem;
    font-weight: 500;
    min-width: 5rem;
  }

  .tir-pm-task__block-title {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &_space {
    position: sticky;
    left: 0;
    top: 0;
    height: 3rem;
    background-color: var(--neutral-0);
    margin-right: -0.125rem;
    z-index: 1;
    cursor: default;
  }
  .tir-pm-task__title {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .task-card-left-part {
    cursor: move;
    position: sticky;
    left: 0 !important;
    top: 0;
    display: flex;
    padding-left: 0.625rem;
    background-color: var(--bg-base, #fff);
    z-index: 1;
    border: 1px solid var(--neutral-translucent-10, #272d371a);
    border-right: none;
    border-top-left-radius: 0.5rem;
    border-bottom-left-radius: 0.5rem;

    &.tir-pm-left-border {
      &::before {
        content: '';
        position: absolute;
        left: 0;
        width: 0.375rem;
        height: 100%;
        background-color: orange;
        border-top-left-radius: 0.5rem;
        border-bottom-left-radius: 0.5rem;
      }

      &.current-queue::before {
        background-color: var(--text-info, #2a77ef);
      }
    }
  }

  .task-card-right-part {
    border: 1px solid var(--neutral-translucent-10, #272d371a);
    background-color: var(--bg-base, #fff);
    border-left: none;
    border-top-right-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
    flex: 1;
  }

  .task-card-left-part,
  .task-card-right-part {
    display: flex;
    align-items: center;
    height: 3rem;
  }

  &_icon {
    max-width: 1rem;
    max-height: 1rem;
    min-width: 1rem;
    min-height: 1rem;
  }

  &_circle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    overflow: hidden;
    background: var(--neutral-translucent-10);
    color: var(--text-primary);
    font-size: 0.75rem;
    font-weight: 400;
  }

  &.over:not(.plug) {
    .task-card-left-part,
    .task-card-right-part {
      border-color: #3a7cc2;
    }
  }

  &.plug {
    &::before {
      display: none;
    }

    border-color: transparent;
    animation: blink 1s infinite;
    transition: background-color 1s ease !important;

    @keyframes blink {
      0% {
        background-color: #fff;
      }

      50% {
        background-color: rgba(39, 45, 55, 0.05);
      }
    }

    & > * {
      opacity: 0;
    }
  }

  &_animation-enter-active,
  &_animation-leave-active {
    transition: 0.3s;
    opacity: 1;
  }

  &_animation-enter-from,
  &_animation-leave-to {
    transition: 0.3s;
    opacity: 0;
  }
}
</style>
